import React, { useState, useEffect, Suspense } from 'react';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import Separator from '../../layout/Separator';
import ErrorBoundary from '../../common/ErrorBoundery';
import RequestService from '../../../services/RequestService';
import appService from '../../../services/ApplicationService';
import { useParams, useNavigate } from 'react-router-dom';
import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import { alpha } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import logo from '../../../assets/images/logo.jpeg';
import LogoLoader from '../../common/LogoLoader';

const RequestSuccess = () => {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [requestDetails, setRequestDetails] = useState({
        requestId: '',
        status: '',
        system: '',
        systemId: null,
        roles: []
    });
    
    const { id } = useParams();
    const navigate = useNavigate();
    const [openSnackbar, setOpenSnackbar] = useState(false);
    
    useEffect(() => {
        const fetchRequestDetails = async () => {
            try {
                setIsLoading(true);
                // Record start time for minimum loading time
                const loadingStartTime = Date.now();
                const minLoadingTime = 2000; // 2 seconds minimum
                
                // Use RequestService to fetch the request details
                const requestData = await RequestService.fetchRequestById(id);
                console.log('Request details retrieved:', requestData);
                
                if (requestData) {
                    setRequestDetails({
                        requestId: requestData.REQUEST_ID || 'N/A',
                        status: requestData.STATUS || 'Submitted',
                        system: requestData.APP_NAME || 'N/A',
                        roles: requestData.ROLES ? requestData.ROLES.split(',') : []
                    });
                } else {
                    console.error('No request details returned');
                    setError('The request details could not be loaded.');
                }
                
                // Calculate how much time to wait to meet minimum loading time
                const elapsedTime = Date.now() - loadingStartTime;
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
                
                // Ensure we show loading state for at least minLoadingTime
                if (remainingTime > 0) {
                    await new Promise(resolve => setTimeout(resolve, remainingTime));
                }
                
            } catch (error) {
                console.error('Error fetching request details:', error);
                setError(error.message || 'Failed to load request details. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchRequestDetails();
    }, [id]);

    const handleCopy = () => {
        navigator.clipboard.writeText(requestDetails.requestId)
            .then(() => {
                setOpenSnackbar(true);
            })
            .catch((err) => {
                console.error('Failed to copy text: ', err);
            });
    };
    
    const handleGoBack = () => {
        navigate('/myrequests');
    };

    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    const customMainContent = (
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f7' }}>
            <ResponsiveContainer>
                {/* Simplified header with logo similar to sidebar */}
                <mui.Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: '#0D6EFD', 
                        color: 'white',
                        p: 1,
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        mb: 2,
                        mt: 3
                    }}
                >
                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src={logo} width="35" height="35" alt="Audit-AI Logo" style={{ marginRight: '10px' }} />
                        <mui.Typography variant="h6" sx={{ fontSize: '25px' }}>
                            Audit-AI
                        </mui.Typography>
                    </mui.Box>
                </mui.Box>
                <Separator />

                {/* Back navigation */}
                <mui.Box sx={{ mt: 2, mb: 1 }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link 
                            underline="hover" 
                            color="inherit" 
                            onClick={() => navigate('/myrequests')}
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' }
                            }}
                        >
                            <mui.Icon sx={{ mr: 0.5, fontSize: '20px' }}>arrow_back</mui.Icon>
                            Back to My Requests
                        </mui.Link>
                    </mui.Breadcrumbs>
                </mui.Box>

                {isLoading ? (
                    <mui.Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '60vh',
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        <LogoLoader size={60} message="Loading request details..." />
                    </mui.Box>
                ) : (
                    <mui.Box 
                        sx={{ 
                            maxWidth: '700px',
                            margin: '20px auto',
                            padding: { xs: '0 16px', md: 0 }
                        }}
                    >
                        <mui.Paper 
                            elevation={0} 
                            sx={{ 
                                borderRadius: '16px',
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                background: '#ffffff',
                                mb: 4
                            }}
                        >
                            <mui.Box 
                                sx={{ 
                                    bgcolor: '#4caf50',
                                    color: 'white',
                                    py: 3,
                                    px: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}
                            >
                                <CheckCircleOutlineRoundedIcon fontSize="medium" />
                                <mui.Box>
                                    <mui.Typography variant="h6" fontWeight={500}>
                                        Request Submitted
                                    </mui.Typography>
                                    <mui.Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                        Your access request has been processed successfully
                                    </mui.Typography>
                                </mui.Box>
                            </mui.Box>
                            
                            <mui.Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 3, sm: 4 } }}>
                                <mui.Box 
                                    sx={{ 
                                        mb: 4,
                                        p: 3,
                                        bgcolor: alpha('#f5f5f5', 0.5),
                                        borderRadius: 2,
                                        border: '1px dashed',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Reference Number
                                        </mui.Typography>
                                        
                                        <mui.Tooltip title="Copy Reference">
                                            <mui.IconButton 
                                                onClick={handleCopy} 
                                                size="small"
                                                sx={{ 
                                                    color: '#1976d2',
                                                    bgcolor: alpha('#fff', 0.8),
                                                    '&:hover': { bgcolor: '#fff' }
                                                }}
                                            >
                                                <ContentCopyRoundedIcon fontSize="small" />
                                            </mui.IconButton>
                                        </mui.Tooltip>
                                    </mui.Box>
                                    
                                    <mui.Typography 
                                        variant="h6" 
                                        fontWeight="500" 
                                        sx={{ 
                                            mt: 1,
                                            letterSpacing: '0.5px',
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {requestDetails.requestId}
                                    </mui.Typography>
                                </mui.Box>
                                
                                <mui.Box sx={{ mb: 4 }}>
                                    <mui.Typography variant="subtitle2" fontWeight="500" gutterBottom>
                                        Request Summary
                                    </mui.Typography>
                                    
                                    <mui.Box 
                                        sx={{ 
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: 2,
                                            mt: 2
                                        }}
                                    >
                                        <mui.Box>
                                            <mui.Typography variant="caption" color="text.secondary">
                                                Status
                                            </mui.Typography>
                                            <mui.Typography variant="body2" sx={{ mt: 0.5 }}>
                                                <mui.Box 
                                                    component="span" 
                                                    sx={{ 
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        bgcolor: requestDetails.status === 'Approved' ? 
                                                            alpha('#4caf50', 0.1) : alpha('#1976d2', 0.1),
                                                        color: requestDetails.status === 'Approved' ? 
                                                            '#4caf50' : '#1976d2'
                                                    }}
                                                >
                                                    {requestDetails.status}
                                                </mui.Box>
                                            </mui.Typography>
                                        </mui.Box>
                                        
                                        <mui.Box>
                                            <mui.Typography variant="caption" color="text.secondary">
                                                System
                                            </mui.Typography>
                                            <mui.Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {requestDetails.system}
                                            </mui.Typography>
                                        </mui.Box>
                                        
                                        <mui.Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
                                            <mui.Typography variant="caption" color="text.secondary">
                                                Requested Roles
                                            </mui.Typography>
                                            <mui.Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {requestDetails.roles.length > 0 ? (
                                                    requestDetails.roles.map((role, index) => (
                                                        <mui.Chip 
                                                            key={index}
                                                            label={role} 
                                                            size="small"
                                                            sx={{ 
                                                                bgcolor: alpha('#1976d2', 0.1),
                                                                color: 'text.primary',
                                                                fontWeight: 400,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    ))
                                                ) : (
                                                    <mui.Typography variant="body2" color="text.secondary">
                                                        No roles specified
                                                    </mui.Typography>
                                                )}
                                            </mui.Box>
                                        </mui.Box>
                                    </mui.Box>
                                </mui.Box>
                                
                                <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Save the reference number above if you need to follow up on this request in the future.
                                </mui.Typography>
                                
                                <mui.Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <mui.Button
                                        variant="outlined"
                                        onClick={handleGoBack}
                                        startIcon={<KeyboardBackspaceRoundedIcon />}
                                        sx={{ 
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            px: 3,
                                            py: 1,
                                            borderColor: '#1976d2',
                                            color: '#1976d2',
                                            '&:hover': {
                                                borderColor: '#1565c0',
                                                bgcolor: alpha('#1976d2', 0.04)
                                            }
                                        }}
                                    >
                                        Return to My Requests
                                    </mui.Button>
                                </mui.Box>
                            </mui.Box>
                        </mui.Paper>
                    </mui.Box>
                )}

                <mui.Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <mui.Alert 
                        onClose={handleSnackbarClose} 
                        severity="success" 
                        elevation={0}
                        variant="filled"
                        sx={{ 
                            borderRadius: '8px',
                            fontWeight: 500
                        }}
                    >
                        Reference number copied to clipboard
                    </mui.Alert>
                </mui.Snackbar>
            </ResponsiveContainer>
        </div>
    );

    return (
        <ErrorBoundary fallback={<p>Failed to load data.</p>} error={error}>
            <Suspense fallback={
                <mui.Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    flexDirection: 'column',
                    gap: 2
                }}>
                    <LogoLoader size={60} message="Loading access request details..." />
                </mui.Box>
            }>
                {customMainContent}
            </Suspense>
        </ErrorBoundary>
    );
};

export default RequestSuccess;
